import type { stacks } from "@dropbox/api-v2-client";
import { Button } from "@dropbox/dig-components/buttons";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronLeftLine, PinFill, PinLine } from "@dropbox/dig-icons/assets";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { upsertToggleStackIsPinned } from "@mirage/service-stacks";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { showSnackbar } from "@mirage/shared/snackbar";
import { useStackPaths } from "@mirage/stacks/paths";
import i18n from "@mirage/translations";
import { NavLink } from "react-router-dom";
import { featureLine } from "../constants";
import { useReportToggleStackIsPinned } from "../hooks";
import { useShareModal } from "../ShareModal/ShareModal";
import { ShareStackButton } from "./ShareStackButton";
import { StackAskDashButton } from "./StackAskDashButton";
import { StackMembersFacepile } from "./StackMembersFacepile";
import styles from "./StackNavBar.module.css";
import { StackNavBarActions } from "./StackNavBarActions";

const actionSurfaceComponent = "stack_header";

export const StackNavBar = ({ stack }: { stack: stacks.Stack | null }) => {
  const stackPaths = useStackPaths();
  const { openShareModal, shareModalJsx } = useShareModal({ stack });

  const isNewStack = stack === null;

  const handleStackMembersFacepileClick = () => {
    if (!stack || !stack?.namespace_id) return;
    openShareModal();
  };

  return (
    <div className={styles.stackNavBar}>
      <div className={styles.stackNavBarLeft}>
        <NavLink to={stackPaths.stacks()}>
          <Button
            variant="borderless"
            withIconStart={<UIIcon src={ChevronLeftLine} />}
          >
            {i18n.t("save_and_exit_stack")}
          </Button>
        </NavLink>
      </div>
      {stack?.namespace_id && (
        <Box
          alignItems="center"
          columnGap="Micro XSmall"
          display="flex"
          flexDirection="row"
        >
          <StackMembersFacepile
            onClick={handleStackMembersFacepileClick}
            stack={stack}
          />
          <div className={styles.actionsSeparator} />
          <TogglePinButton stack={stack} />
          <StackNavBarActions className={styles.settingsButton} stack={stack} />
          <ShareStackButton
            className={styles.shareButton}
            disabled={isNewStack}
            iconType="none"
            onClick={openShareModal}
            size="large"
            stack={stack}
          />
          <StackAskDashButton stack={stack} />
        </Box>
      )}
      {shareModalJsx}
    </div>
  );
};

const TogglePinButton = ({ stack }: { stack: stacks.Stack }) => {
  const { reportToggleStackIsPinned } = useReportToggleStackIsPinned();
  const { isEmbed } = useStackPaths();

  const handlePinClick = () => {
    reportToggleStackIsPinned(stack, actionSurfaceComponent);
    upsertToggleStackIsPinned(
      { type: "nsId", id: stack.namespace_id! },
      isEmbed ? "fss_stack" : undefined,
    );
    showSnackbar({
      title: i18n.t(
        stack.user_data?.is_pinned ? "stack_unpinned" : "stack_pinned",
      ),
    });
  };

  return (
    <IconButtonWithTooltip
      data-uxa-entity-id={stack?.namespace_id}
      data-uxa-log={createUxaElementId("navbar_pin_stack_button", {
        actionSurfaceComponent,
        featureLine,
      })}
      onClick={handlePinClick}
      size="large"
      style={{ padding: "8px" }}
      variant="borderless"
      tooltipProps={{
        title: i18n.t(stack.user_data?.is_pinned ? "unpin_stack" : "pin_stack"),
      }}
    >
      <UIIcon src={stack.user_data?.is_pinned ? PinFill : PinLine} />
    </IconButtonWithTooltip>
  );
};
